import moment from 'moment'
import { RelationHospital, RelationArea, AdmissionPartner, AdmissionProperties, AdmissionUser } from '@/infrastructure/network/Monitaro/schema/models'

export interface IAdmissionProps {
  id?: string
  user?: AdmissionUser | null
  userId?: string | null
  hospital?: RelationHospital | null
  hospitalId?: string | null
  area?: RelationArea | null
  areaId?: string | null
  properties: AdmissionProperties | null
  staytime: number | null
  checkinAt: string | null
  checkoutAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface IAdmissionQueryProps {
  started: string | null
  ended: string | null
  staytime: { duration: string | null; range: string } | null
}

export default class AdmissionEntity {
  private _props: IAdmissionProps

  constructor(props?: IAdmissionProps | null) {
    this._props = EmptyAdmissionPropsFactory(props)
  }

  get props(): IAdmissionProps {
    return this._props
  }

  get clone(): IAdmissionProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get hospitalId(): string | null | undefined {
    return this._props.hospitalId
  }

  get userId(): string | null | undefined {
    return this._props.userId
  }

  get areaId(): string | null | undefined {
    return this._props.areaId
  }

  get staytime(): string {
    if (this._props.staytime) {
      const duration = moment.duration(this._props.staytime, 'seconds')
      const hours = duration.hours()
      const hour = hours > 0 ? `${duration.hours()}時間` : ''
      const minutes = duration.minutes()
      const minute = minutes > 0 ? `${duration.minutes()}分` : '0分'
      return `${hour}${minute}`
    }
    return '-'
  }

  get checkinAt(): string | null {
    return this._props.checkinAt
  }

  get checkoutAt(): string | null {
    return this._props.checkoutAt
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get user(): AdmissionUser | null | undefined {
    return this._props.user
  }

  get hospital(): RelationHospital | null | undefined {
    return this._props.hospital
  }

  get area(): RelationArea | null | undefined {
    return this._props.area
  }

  get properties(): AdmissionProperties | null | undefined {
    return this._props.properties
  }

  get partners(): string[] {
    return this._props.properties?.partners?.map((partner) => (partner.name || '') + (partner.temperature ? `:${partner.temperature}℃` : '')) || []
  }

  get date(): string | null {
    return moment(this._props.checkinAt).format('YYYY/MM/DD')
  }

  get checkin(): string | null {
    return moment(this._props.checkinAt).format('H:mm')
  }

  get checkout(): string | null {
    return this._props.checkoutAt ? moment(this._props.checkoutAt).format('H:mm') : '-'
  }

  get tag(): string | null {
    return this._props.properties?.tag || null
  }
}

export const headers = [
  { text: '日付', value: 'date', sortable: true, class: 'min' },
  { text: '訪問先', value: 'hospital', sortable: true },
  { text: '場所', value: 'area', sortable: true },
  { text: 'IN', value: 'checkin', class: 'text-right', sortable: true },
  { text: 'OUT', value: 'checkout', class: 'text-right', sortable: true },
  { text: '滞在時間', value: 'staytime', class: 'text-right', sortable: true },
  { text: '入館番号', value: 'tag', class: 'text-center' },
  { text: '同行者', value: 'partners' }
]

export const EmptyAdmissionPropsFactory = (props?: Partial<IAdmissionProps> | null): IAdmissionProps => ({
  staytime: null,
  checkinAt: null,
  checkoutAt: null,
  createdAt: null,
  updatedAt: null,
  ...props,
  properties: EmptyAdmissionPropertiesFactory(props?.properties)
})

export const EmptyAdmissionPropertiesFactory = (props?: Partial<AdmissionProperties> | null): AdmissionProperties => ({
  tag: null,
  destination: null,
  purpose: null,
  temperature: null,
  partners: props?.partners?.map((prop) => EmptyAdmissionPartnerFactory(prop)) || [],
  ...props
})

export const EmptyAdmissionPartnerFactory = (props?: Partial<AdmissionPartner> | null): AdmissionPartner => ({
  name: null,
  temperature: null,
  ...props
})

export const EmptyAdmissionQueryPropsFactory = (props?: Partial<IAdmissionQueryProps> | null): IAdmissionQueryProps => ({
  started: null,
  ended: null,
  staytime: { duration: null, range: '>=' },
  ...props
})
